/* eslint-disable */

export type QueryParamsType = Record<string | number, any>;
export type ResponseFormat = keyof Pick<Body, 'json' | 'formData'>;

export interface FullRequestParams extends Omit<RequestInit, 'body'> {
  // secure를 `true`로 설정하면 `secureWorker`를 호출합니다.
  secure?: boolean;
  /** request path */
  path: string;
  /** request body 타입 eg) json, formData */
  type?: ContentType;
  /** query params */
  query?: QueryParamsType;
  /** response 타입 eg) res.json() -> format: "json" */
  format?: ResponseFormat;
  /** request body */
  body?: unknown;
  /** base url */
  baseURL?: string;
  /** 요청 취소 토큰 */
  timeout?: number;
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>;

export interface ApiConfig<SecureDataType = unknown> {
  baseURL?: string;
  secureWorker?: (
    securityData: SecureDataType | null,
  ) => Promise<RequestParams | void> | RequestParams | void;
  secure?: boolean;
  customFetch?: typeof fetch;
  defaultHeaders?: Record<string, string>;
}

export interface StatusResult {
  /** @format int32 */
  status: number;
  message: string;
  code: string;
}

export interface StatusResultWithBody extends StatusResult {
  __requestBody?: unknown;
}

export interface ErrorResult {
  /** @format int32 */
  status: number;
  message: string;
  code: string;
}

interface ResourceResponse<T = {}> {
  data: T;
  meta: Record<string, object>;
  result: StatusResult;
}

export interface HttpResponse<D, E = unknown> extends Response {
  data: D;
  error: E;
}

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
}

export class HttpClient<SecureDataType = unknown> {
  public baseURL = '';

  private secureData: SecureDataType | null = null;

  private secureWorker?: ApiConfig<SecureDataType>['secureWorker'];

  private customFetch = (...fetchParams: Parameters<typeof fetch>) => fetch(...fetchParams);

  private defaultHeaders: Record<string, string> = {};

  private baseApiParams: RequestParams = {
    credentials: 'include',
    headers: {},
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  };

  constructor(apiConfig: ApiConfig<SecureDataType> = {}) {
    Object.assign(this, apiConfig);
  }

  public setSecurityData = (data: SecureDataType | null) => {
    this.secureData = data;
  };

  protected encodeQueryParam(key: string, value: any) {
    const encodedKey = encodeURIComponent(key);
    return `${encodedKey}=${encodeURIComponent(typeof value === 'number' ? value : `${value}`)}`;
  }

  protected addQueryParam(query: QueryParamsType, key: string) {
    return this.encodeQueryParam(key, query[key]);
  }

  protected addArrayQueryParam(query: QueryParamsType, key: string) {
    const value = query[key];
    return value.map((v: any) => this.encodeQueryParam(key, v)).join('&');
  }

  protected toQueryString(rawQuery?: QueryParamsType): string {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter((key) => 'undefined' !== typeof query[key]);
    return keys
      .map((key) =>
        Array.isArray(query[key])
          ? this.addArrayQueryParam(query, key)
          : this.addQueryParam(query, key),
      )
      .join('&');
  }

  protected addQueryParams(rawQuery?: QueryParamsType): string {
    const queryString = this.toQueryString(rawQuery);
    return queryString ? `?${queryString}` : '';
  }

  private contentFormatters: Record<ContentType, (input: any) => any> = {
    [ContentType.Json]: (input: any) =>
      input !== null && (typeof input === 'object' || typeof input === 'string')
        ? JSON.stringify(input)
        : input,
    [ContentType.FormData]: (input: any) =>
      Object.keys(input || {}).reduce((formData, key) => {
        const property = input[key];
        formData.append(
          key,
          property instanceof Blob
            ? property
            : typeof property === 'object' && property !== null
            ? JSON.stringify(property)
            : `${property}`,
        );
        return formData;
      }, new FormData()),
  };

  protected combineRequestParams(params1: RequestParams, params2?: RequestParams): RequestParams {
    return {
      ...this.baseApiParams,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(this.baseApiParams.headers || {}),
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {}),
      },
    };
  }

  protected ensureAcceptJson(requestParams: RequestParams): string {
    const existingAccept =
      (requestParams.headers as Record<string, string | string[]> | undefined)?.accept ||
      this.defaultHeaders?.accept ||
      '';

    const acceptHeaderValue = Array.isArray(existingAccept)
      ? existingAccept.join(', ')
      : typeof existingAccept === 'string'
      ? existingAccept
      : '';

    const hasJsonInAccept = acceptHeaderValue
      .toLowerCase()
      .split(',')
      .some((type) => type.trim().includes('application/json'));

    return hasJsonInAccept
      ? acceptHeaderValue
      : acceptHeaderValue
      ? `${acceptHeaderValue}, application/json`
      : 'application/json';
  }

  public request = async <T = any, E = any>({
    body,
    secure,
    path,
    type = ContentType.Json,
    query,
    format = 'json',
    baseURL,
    timeout = 30000,
    signal: externalSignal,
    ...params
  }: FullRequestParams) => {
    const secureParams =
      ((typeof secure === 'boolean' ? secure : this.baseApiParams.secure) &&
        this.secureWorker &&
        (await this.secureWorker(this.secureData))) ||
      {};
    const requestParams = this.combineRequestParams(params, secureParams);
    const queryString = query && this.toQueryString(query);
    const payloadFormatter = this.contentFormatters[type || ContentType.Json];
    const responseFormat = format || requestParams.format;

    const abortController = new AbortController();
    const abortTimeout = setTimeout(() => abortController.abort(), timeout);

    const relayController = new AbortController();

    const handleAbort = () => relayController.abort();

    if (externalSignal) {
      if (externalSignal.aborted) {
        handleAbort();
      } else {
        externalSignal.addEventListener('abort', handleAbort);
      }
    }

    abortController.signal.addEventListener('abort', handleAbort);
    try {
      const response = (await this.customFetch(
        `${baseURL || this.baseURL || ''}${path}${queryString ? `?${queryString}` : ''}`,
        {
          ...requestParams,
          headers: {
            ...this.defaultHeaders,
            ...(requestParams.headers || {}),
            ...(type && type !== ContentType.FormData ? { 'Content-Type': type } : {}),
            accept: this.ensureAcceptJson(requestParams),
          },
          signal: relayController.signal,
          body: typeof body === 'undefined' || body === null ? null : payloadFormatter(body),
        },
      )) as HttpResponse<T, E>;

      clearTimeout(abortTimeout);

      response.data = null as T;
      response.error = null as E;

      // const resourceResponse = response.data as ResourceResponse;

      const data = !responseFormat
        ? response
        : await response[responseFormat]()
            .then((data) => {
              const resourceResponse = data as ResourceResponse;

              if (!resourceResponse?.result?.status || resourceResponse?.result?.status >= 400) {
                throw resourceResponse.result;
              }
              if (response.ok) {
                response.data = data;
              }
              return response;
            })
            .catch((err) => {
              const method = (params.method || '').toLowerCase();
              if (!!err && typeof err === 'object' && ['put', 'delete', 'post'].includes(method)) {
                (err as any).__requestBody = body;
              }
              throw err;
            });

      if (!response.ok) {
        throw data;
      }

      return data.data;
    } finally {
      externalSignal?.removeEventListener('abort', handleAbort);
      abortController.signal.removeEventListener('abort', handleAbort);
      clearTimeout(abortTimeout);
    }
  };
}

export function isStatusResultError(obj: unknown): obj is StatusResult {
  return typeof obj === 'object' && obj !== null && 'status' in obj && 'code' in obj;
}
